import { ConfigService } from '@stockcare-services/config-service/config.service';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { JwtHelperService } from '@auth0/angular-jwt';
import { from, Observable, Subscription } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';
import { IRequest } from '@stockcare-models/IRequest';
import { IToken } from '@stockcare-models/IToken';
import { MatDialog } from '@angular/material/dialog';
import { UserStorageService } from '@stockcare-storage/user/user-storage.service';
import { CryptoService } from '@stockcare-services/crypto/crypto.service';
import { UsersService } from '@stockcare-services/users-service/users.service';
import { AuthenticationStateService } from './authentication-state.service';
import { Roles } from '@stockcare-helpers/roles';
import { environment } from '@stockcare-environments/environment';

@Injectable({
  providedIn: 'root',
})
export class AuthenticationService {
  isUserLoggedIn: boolean = false;
  isActionCenterLogin: boolean = false;
  subscriptions = new Subscription();

  constructor(
    private http: HttpClient,
    private jwtHelperService: JwtHelperService,
    private conf: ConfigService,
    private dialogRef: MatDialog,
    private userStorageService: UserStorageService,
    private cryptoService: CryptoService,
    private userService: UsersService,
    private authenticationStateService: AuthenticationStateService
  ) {
    this.authenticationStateService.updateLoggedInState(localStorage.getItem('token') !== null);
    this.subscriptions.add(
      this.authenticationStateService.isLoggedIn().subscribe((isUserLoggedIn) => {
        this.isUserLoggedIn = isUserLoggedIn;
      })
    );
  }

  isAdministrator(): boolean {
    return this.isInRole([Roles.systemAdministrator, Roles.superAdministrator, Roles.hospitalAdministrator]);
  }

  isHospitalAdmistrator(): boolean {
    return this.isInRole([Roles.hospitalAdministrator]);
  }

  isSuperAdmin(): boolean {
    return this.isInRole([Roles.systemAdministrator, Roles.superAdministrator]);
  }

  isCreditApprover(): boolean {
    return this.isInRole([Roles.systemAdministrator, Roles.superAdministrator, Roles.hospitalAdministrator, Roles.creditApprover]);
  }

  isTheatreUser(): boolean {
    return this.isInRole([Roles.systemAdministrator, Roles.superAdministrator, Roles.hospitalAdministrator, Roles.theatre]);
  }

  isActionCenterUserLoggedIn(): boolean {
    return localStorage.getItem('actionCenterToken') !== null;
  }

  login(request: IRequest): Observable<IToken> {
    return from(this.cryptoService.encrypt(request.password, 'Xk9#mP2$vQ7@nL4&wR6!jT8*yF3^hN5')).pipe(
      switchMap((encryptedPassword) => {
        const encryptedRequest = { ...request, password: encryptedPassword };
        return this.http.post<IToken>(`${this.conf.config.baseUrl}/token/SignIn`, encryptedRequest);
      }),
      tap((result: IToken) => {
        if (this.isActionCenterLogin) {
          localStorage.setItem('actionCenterToken', result.token);
        } else {
          localStorage.setItem('token', result.token);
          this.saveUser(result.token);
          this.authenticationStateService.updateLoggedInState(true);
          this.userService.updateLastLoginDateTime().subscribe();
        }
      }),
      catchError((error) => {
        if (this.isActionCenterLogin) {
          localStorage.removeItem('actionCenterToken');
        } else {
          localStorage.removeItem('token');
        }
        throw error;
      })
    );
  }

  loginWithPin(username: any, pin: any) {
    return new Observable((observer) => {
      this.userStorageService
        .getUserByUsername(username)
        .then((user) => {
          const isValidPin = this.cryptoService.verifyComputedHash(user.Pin, pin, username);

          if (this.isActionCenterLogin) {
            localStorage.setItem('actionCenterToken', user.Token);
          } else {
            if (isValidPin) {
              localStorage.setItem('token', user.Token);
              this.authenticationStateService.updateLoggedInState(true);
              this.userService.updateLastLoginDateTime().subscribe();
              this.userStorageService.updateUserLoggedInDate(user);
            } else {
              this.authenticationStateService.updateLoggedInState(false);
            }
          }
          observer.next(isValidPin);
        })
        .catch((error) => observer.error(error));
    });
  }

  logout() {
    if (this.isActionCenterLogin) {
      localStorage.removeItem('actionCenterToken');
      this.isActionCenterLogin = false;
    } else {
      this.authenticationStateService.updateLoggedInState(false);
      this.dialogRef.closeAll();
      localStorage.removeItem('token');
    }
  }

  isTokenExpired(token: string): boolean {
    return this.jwtHelperService.isTokenExpired(token);
  }

  decodeToken(token: string) {
    return this.jwtHelperService.decodeToken(token);
  }

  getToken() {
    return localStorage.getItem('token');
  }

  getUserId() {
    const token = this.decodeToken(this.getToken()!);

    return token.nameid;
  }

  getUsername() {
    const token = this.decodeToken(this.getToken()!);

    return token.unique_name;
  }

  isInRole(roles: string[]): boolean {
    const token = this.decodeToken(this.getToken()!);

    if (!Array.isArray(token.role)) {
      return roles.indexOf(token.role) >= 0;
    }

    return token.role.some((role: string) => roles.indexOf(role) >= 0);
  }

  async saveUser(token: string) {
    const decodedToken = this.decodeToken(token);
    const username = decodedToken.unique_name;
    const existingUser = await this.userStorageService.checkUserExistsInDb(username);
    if (!existingUser) {
      this.userStorageService.createNewUser(token, username);
    } else {
      existingUser.Token = token;
      existingUser.LastAuthenticationDate = new Date();
      this.userStorageService.updateUser(existingUser);
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
